<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.edit-regular"></svg-vue>
          <h1 class="content-title">
            {{ $t('Edit custom bill') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <button @click="deleteCustomBillModal = true" class="btn btn-red rounded-none rounded-l-md" type="button">
            {{ $t('Delete') }}
          </button>
          <router-link class="btn btn-app rounded-none" :to="`/print/custom-bill/${bill.uuid}`">
            {{ $t('Print') }}
          </router-link>
          <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin/custom-bills">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <loading :status="loading"></loading>
      <form @submit.prevent="updateBill">
        <div class="grid grid-cols-10 gap-2">
          <loading :status="loading"></loading>
          <div class="col-span-10 md:col-span-7 content-card-body">
            <div class="md:grid md:grid-cols-10 md:gap-2 px-4 py-5 sm:p-6">
              <div class="md:col-span-3">
                <h3 class="form-description-head">
                  {{ $t('Item information') }}
                </h3>
                <p class="form-description-text">{{ $t('Enter item and related information') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-7">
                <div class="grid grid-cols-12 gap-1">
                  <div class="col-span-12">
                    <div class="relative rounded-md shadow-sm">
                      <input :placeholder="$t('Item name')" class="form-input" id="name" v-model="item.name" />
                    </div>
                  </div>
                  <div class="col-span-12 md:col-span-6">
                    <div class="relative rounded-md shadow-sm">
                      <input :placeholder="$t('Quantity')" type="number" class="form-input" id="qty" v-model="item.qty" />
                    </div>
                  </div>
                  <div class="col-span-12 md:col-span-6">
                    <div class="relative rounded-md shadow-sm">
                      <input :placeholder="$t('Price')" class="form-input" step=".01" type="number" id="price" v-model="item.price" />
                    </div>
                  </div>
                  <div class="col-span-12 md:col-span-6">
                    <div class="relative rounded-md shadow-sm">
                      <input :placeholder="$t('IMEI')" class="form-input" id="imei" v-model="item.imei" />
                    </div>
                  </div>

                  <div class="col-span-12 md:col-span-6">
                    <div class="relative rounded-md shadow-sm">
                      <input :placeholder="$t('Serial number')" class="form-input" id="serial_number" v-model="item.serial_number" />
                    </div>
                  </div>
                  <div class="col-span-12 pt-1">
                    <button class="btn btn-app rounded-none rounded-l-md" :disabled="checking" type="button" @click.prevent="addItem">
                      {{ $t('Add') }}
                    </button>
                    <button class="btn btn-app rounded-none rounded-r-md" type="button" @click.prevent="clearItems">
                      {{ $t('Undo') }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-span-12 mt-2 overflow-x-auto">
                <div class="overflow-hidden">
                  <table class="min-w-full divide-y text-left divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="w-1/2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('Item Name') }}
                        </th>
                        <th scope="col" class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('IMEI') }}
                        </th>
                        <th scope="col" class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('SN') }}
                        </th>
                        <th scope="col" class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('QTY') }}
                        </th>
                        <th scope="col" class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('Price') }}
                        </th>
                        <th scope="col" class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('Sub total') }}
                        </th>
                        <th scope="col" class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {{ $t('Action') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="item in bill.items" :key="item.name">
                        <td class="py-4 whitespace-nowrap w-1/2">{{ item.name | capitalize }}</td>
                        <td class="py-4 whitespace-nowrap">{{ item.imei }}</td>
                        <td class="py-4 whitespace-nowrap">{{ item.serial_number }}</td>
                        <td class="py-4 whitespace-nowrap">{{ item.qty }}</td>
                        <td class="py-4 whitespace-nowrap">{{ item.price | currency }}</td>
                        <td class="py-4 whitespace-nowrap">{{ item.sub_total | currency }}</td>
                        <td class="py-4 whitespace-nowrap pl-4">
                          <button class="w-3 h-3 text-red-400 cursor-pointer" @click.prevent="removeItem(item)">
                            <svg-vue icon="font-awesome.trash-alt-regular"></svg-vue>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="col-span-10 md:col-span-3 content-card-body px-4 py-5 sm:p-6">
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <label class="form-label" for="name">{{ $t('Name') }}</label>
                  <div class="relative rounded-md shadow-sm">
                    <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="bill.name" />
                  </div>
                </div>
              </div>
              <div class="col-span-3 mt-2">
                <label class="form-label" for="description">{{ $t('Description') }}</label>
                <div class="relative rounded-md shadow-sm">
                  <textarea class="form-input" id="description" v-model="bill.description" rows="5"></textarea>
                </div>
              </div>
              <div class="col-span-3 mt-2">
                <label class="form-label" for="date">{{ $t('Date') }}</label>
                <div class="relative rounded-md shadow-sm">
                  <input :placeholder="$t('Date')" class="form-input" type="date" id="date" v-model="bill.date" />
                </div>
              </div>
              <div class="col-span-3 mt-2">
                <label class="form-label" for="time">{{ $t('Time') }}</label>
                <div class="relative rounded-md shadow-sm">
                  <input :placeholder="$t('Time')" class="form-input" type="time" id="time" v-model="bill.time" />
                </div>
              </div>
              <div class="col-span-3 mt-2">
                <label class="form-label" for="charges">{{ $t('Charges') }}</label>
                <div class="relative rounded-md shadow-sm">
                  <input :placeholder="$t('Charges')" type="number" step=".01" class="form-input" id="charges" v-model="bill.charges" />
                </div>
              </div>
            </div>

            <div class="col-span-3 mt-2">
              <div class="flex justify-between">
                <strong>{{ $t('Charges') }} :</strong> {{ bill.charges | currency }}<br />
              </div>
              <div class="flex justify-between">
                <strong> {{ $t('Tax') }} :</strong> {{ taxAmount | currency }}<br />
              </div>
              <div class="flex justify-between">
                <strong>{{ $t('After tax') }} :</strong>
                {{ afterTax | currency }}<br />
              </div>
            </div>
          </div>
        </div>
        <div class="card-footer mt-2 overflow-hidden rounded-md shadow">
          <span class="inline-flex">
            <router-link class="btn btn-app mr-4" to="/admin/custom-bills">{{ $t('Cancel') }}</router-link>
            <button class="btn btn-app" :disabled="loading" type="submit">
              {{ $t('Update') }}
            </button>
          </span>
        </div>
      </form>
    </div>
    <repair-delete-model :show="deleteCustomBillModal" @cancelled="deleteCustomBillModal = false" @confirmed="deleteBill">
      <template #title>{{ $t('Delete custom bill') }}</template>
      {{ $t('Are you sure you want to delete the custom bill ?') }}
    </repair-delete-model>
  </main>
</template>
<script>
import confirmationModel from '@/components/layout/admin/confirmation';

export default {
  name: 'edit',
  metaInfo() {
    return {
      title: this.$i18n.t('Edit custom bill'),
    };
  },
  components: {
    RepairDeleteModel: confirmationModel,
  },
  data() {
    return {
      loading: true,
      deleteCustomBillModal: false,
      item: {},
      bill: {
        date: null,
        charges: 0,
        tax: {
          rate: 0,
          is_tax_included: false,
          is_tax_fix: false,
        },
        items: [],
        time: '',
      },
    };
  },
  computed: {
    checking() {
      const item = this.$data.item;
      return !item.name || !item.qty || !item.price;
    },
    afterTax() {
      if (this.bill.tax.is_tax_included) {
        return Number(this.bill.charges);
      }
      return Number(this.bill.charges) + Number(this.taxAmount);
    },
    charges() {
      let value = 0;
      this.bill.items.forEach((item) => {
        value += Number(item.price * item.qty);
      });
      return value;
    },
    taxAmount() {
      const amount = this.bill.charges;
      if (this.bill.tax.is_vat) {
        let rating = '1.' + this.bill.tax.tax_rate ?? this.bill.tax.rate;
        if (this.bill.tax.is_tax_included) {
          return Number(amount - Number(amount / rating));
        } else {
          return Number(Number(amount * rating) - amount);
        }
      } else {
        const rate = Number(this.bill.tax.tax_rate ?? this.bill.tax.rate);
        if (this.bill.tax.is_tax_fix) {
          return rate;
        }
        return amount * (rate / 100);
      }
    },
  },
  watch: {
    charges() {
      this.bill.charges = this.charges;
    },
    item() {
      this.bill.charges = this.charges;
    },
  },
  mounted() {
    this.getBill();
  },
  methods: {
    removeItem(item) {
      this.bill.items.splice(this.bill.items.indexOf(item), 1);
    },

    clearItems() {
      this.bill.items = [];
      this.bill.charges = 0;
    },
    addItem() {
      this.item.sub_total = this.item.qty * this.item.price;
      this.bill.items.push(this.$data.item);
      this.$data.item = {};
    },
    updateBill() {
      this.loading = true;
      this.bill.tax_amount = this.taxAmount;
      this.bill.total_amount = this.afterTax;
      axios
        .put(route('custom-bills.update', this.$route.params.id), this.bill)
        .then((response) => {
          this.loading = false;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getBill() {
      this.loading = true;
      axios
        .get(route('custom-bills.show', this.$route.params.id))
        .then((response) => {
          this.bill = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    deleteBill() {
      axios
        .delete(route('custom-bills.destroy', this.$route.params.id))
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push('/admin/custom-bills');
        })
        .catch(() => {
          this.deleteCustomBillModal = false;
        });
    },
  },
};
</script>
